/**
 * ConsentContext - Convex-based Privacy Consent Management
 *
 * This context manages GDPR consent preferences using Convex for authenticated users
 * and cookies/localStorage for anonymous users.
 *
 * Flow:
 * - Anonymous users: Consent stored in cookies (primary) + localStorage (fallback)
 * - Authenticated users: Consent stored in Convex database with audit logging
 * - On login: Anonymous consent is transferred to the database
 *
 * MIGRATION: This context now uses Convex directly instead of REST API calls.
 * The cookieConsentManager is only used for anonymous consent cookie management.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { api } from "@careercard/convex/convex/_generated/api";
import { cookieConsentManager } from "../utils/cookieConsent";
import { createLogger } from "../utils/logger-ssr-safe";
import type {
  ConsentPreferences,
  ConsentType,
  PrivacySettings,
} from "../types/privacy";
import { DEFAULT_CONSENT, isValidConsent } from "../constants/privacy";

const logger = createLogger({ module: "ConsentContext" });

interface ConsentContextType {
  // Current consent state
  consent: ConsentPreferences;
  privacySettings: PrivacySettings | null;
  isLoading: boolean;

  // Consent management
  updateConsent: (consent: ConsentPreferences) => Promise<boolean>;
  checkConsent: (consentType: ConsentType) => boolean;
  hasConsent: (consentType: ConsentType) => boolean;

  // Banner state
  showConsentBanner: boolean;
  dismissBanner: () => void;

  // Utilities
  refreshConsent: () => Promise<void>;
  clearCache: () => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

interface ConsentProviderProps {
  children: React.ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({
  children,
}) => {
  // Get Convex auth state
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();

  // Check for existing cookie consent on initial render
  const initialCookieConsent = useMemo(
    () => cookieConsentManager.getConsentCookie(),
    []
  );

  // State
  const [consent, setConsent] = useState<ConsentPreferences>(
    initialCookieConsent || DEFAULT_CONSENT
  );
  const [localPrivacySettings, setLocalPrivacySettings] =
    useState<PrivacySettings | null>(null);
  const [showConsentBanner, setShowConsentBanner] = useState(
    !initialCookieConsent
  );
  const [hasInitialized, setHasInitialized] = useState(false);

  // Convex queries and mutations - conditional on authentication
  const privacySettingsData = useQuery(
    api.privacy.getSettings,
    isAuthenticated ? {} : "skip"
  );

  const updateConsentMutation = useMutation(api.privacy.updateConsent);
  const transferAnonymousConsentMutation = useMutation(
    api.privacy.transferAnonymousConsent
  );

  // Determine loading state
  const isLoading =
    isAuthLoading || (isAuthenticated && privacySettingsData === undefined);

  // Transform Convex privacy settings to match expected PrivacySettings type
  const privacySettings = useMemo((): PrivacySettings | null => {
    if (!privacySettingsData) {
      return localPrivacySettings;
    }

    // Map Convex format to frontend format
    return {
      consent: privacySettingsData.consent,
      version: privacySettingsData.version,
      last_updated: privacySettingsData.lastUpdated,
      consentTimestamp: privacySettingsData.consentTimestamp,
      ip_address: privacySettingsData.ipAddress,
      user_agent: privacySettingsData.userAgent,
    };
  }, [privacySettingsData, localPrivacySettings]);

  // Update consent preferences
  const updateConsent = useCallback(
    async (newConsent: ConsentPreferences): Promise<boolean> => {
      try {
        if (!isValidConsent(newConsent)) {
          logger.warn("Invalid consent preferences provided");
          return false;
        }

        if (isAuthenticated) {
          // Authenticated user - save to Convex database
          const result = await updateConsentMutation({
            consent: newConsent,
            userAgent:
              typeof navigator !== "undefined" ? navigator.userAgent : undefined,
          });

          if (result) {
            setConsent(newConsent);
            setShowConsentBanner(false);

            // Also update the cookie to stay in sync
            cookieConsentManager.setConsentCookie(newConsent);
            logger.info(
              "Consent updated for authenticated user via Convex and cookie synchronized"
            );
            return true;
          }

          return false;
        } else {
          // Anonymous user - save to cookie (with localStorage fallback)
          const success = cookieConsentManager.setConsentCookie(newConsent);

          if (success) {
            setConsent(newConsent);
            setShowConsentBanner(false);
            logger.info("Consent updated for anonymous user (cookie)");
            return true;
          }

          // Fallback to localStorage
          try {
            localStorage.setItem(
              "careercard_anonymous_consent",
              JSON.stringify({
                consent: newConsent,
                timestamp: Date.now(),
              })
            );
            setConsent(newConsent);
            setShowConsentBanner(false);
            logger.info("Consent updated for anonymous user (localStorage fallback)");
            return true;
          } catch {
            logger.error("Failed to save anonymous consent");
            return false;
          }
        }
      } catch (error) {
        logger.error("Error updating consent", error);
        return false;
      }
    },
    [isAuthenticated, updateConsentMutation]
  );

  // Check if user has specific consent
  const checkConsent = useCallback(
    (consentType: ConsentType): boolean => {
      return consent[consentType] === true;
    },
    [consent]
  );

  // Alias for checkConsent
  const hasConsent = useCallback(
    (consentType: ConsentType): boolean => {
      return checkConsent(consentType);
    },
    [checkConsent]
  );

  // Dismiss consent banner
  const dismissBanner = useCallback(() => {
    setShowConsentBanner(false);
    // Store dismissal to prevent showing again this session
    sessionStorage.setItem("consent_banner_dismissed", "true");
  }, []);

  // Refresh consent - triggers re-fetch from Convex
  const refreshConsent = useCallback(async () => {
    // For Convex, queries auto-refresh. We just reset initialization to trigger effect.
    setHasInitialized(false);
  }, []);

  // Clear cache (no-op for Convex, but kept for interface compatibility)
  const clearCache = useCallback(() => {
    logger.debug("clearCache called - no-op for Convex");
  }, []);

  // Handle authenticated user consent loading and transfer
  useEffect(() => {
    // Skip if auth is still loading
    if (isAuthLoading) {
      return;
    }

    // Skip if already initialized
    if (hasInitialized) {
      return;
    }

    const handleAuthenticatedUser = async () => {
      if (!isAuthenticated) {
        // Anonymous user - check cookies/localStorage
        const cookieConsent = cookieConsentManager.getConsentCookie();

        if (cookieConsent) {
          setConsent(cookieConsent);
          setShowConsentBanner(false);
          logger.debug("Loaded consent from cookie for anonymous user");
        } else {
          // Check localStorage fallback
          try {
            const stored = localStorage.getItem("careercard_anonymous_consent");
            if (stored) {
              const parsed = JSON.parse(stored);
              if (isValidConsent(parsed.consent)) {
                setConsent(parsed.consent);
                setShowConsentBanner(false);

                // Migrate to cookie
                if (cookieConsentManager.setConsentCookie(parsed.consent)) {
                  localStorage.removeItem("careercard_anonymous_consent");
                  logger.info("Migrated consent from localStorage to cookie");
                }
              } else {
                setShowConsentBanner(true);
              }
            } else {
              setShowConsentBanner(true);
            }
          } catch {
            setShowConsentBanner(true);
          }
        }

        setHasInitialized(true);
        return;
      }

      // Authenticated user - wait for Convex data
      if (privacySettingsData === undefined) {
        // Still loading from Convex
        return;
      }

      if (privacySettingsData) {
        // User has existing privacy settings in database
        setConsent(privacySettingsData.consent);
        setShowConsentBanner(false);

        // Update cookie to match database
        cookieConsentManager.setConsentCookie(privacySettingsData.consent);
        logger.debug("Loaded consent from Convex for authenticated user");
      } else {
        // No settings in database - check if we have a cookie to transfer
        const cookieConsent = cookieConsentManager.getConsentCookie();

        if (cookieConsent) {
          try {
            // Transfer cookie consent to database
            await transferAnonymousConsentMutation({
              consent: cookieConsent,
              userAgent:
                typeof navigator !== "undefined"
                  ? navigator.userAgent
                  : undefined,
            });

            setConsent(cookieConsent);
            setShowConsentBanner(false);
            logger.info("Transferred cookie consent to Convex for new user");
          } catch (error) {
            logger.error("Failed to transfer consent to Convex", error);
            // Use cookie consent anyway
            setConsent(cookieConsent);
            setShowConsentBanner(false);
          }
        } else {
          // Check localStorage fallback
          try {
            const stored = localStorage.getItem("careercard_anonymous_consent");
            if (stored) {
              const parsed = JSON.parse(stored);
              if (isValidConsent(parsed.consent)) {
                try {
                  await transferAnonymousConsentMutation({
                    consent: parsed.consent,
                    userAgent:
                      typeof navigator !== "undefined"
                        ? navigator.userAgent
                        : undefined,
                  });

                  setConsent(parsed.consent);
                  setShowConsentBanner(false);
                  localStorage.removeItem("careercard_anonymous_consent");
                  logger.info(
                    "Transferred localStorage consent to Convex for new user"
                  );
                } catch (error) {
                  logger.error("Failed to transfer localStorage consent", error);
                  setConsent(parsed.consent);
                  setShowConsentBanner(false);
                }
              } else {
                // No valid consent - show banner
                setConsent(DEFAULT_CONSENT);
                setShowConsentBanner(true);
              }
            } else {
              // No consent anywhere - show banner
              setConsent(DEFAULT_CONSENT);
              setShowConsentBanner(true);
            }
          } catch {
            setConsent(DEFAULT_CONSENT);
            setShowConsentBanner(true);
          }
        }
      }

      setHasInitialized(true);
    };

    handleAuthenticatedUser();
  }, [
    isAuthLoading,
    isAuthenticated,
    privacySettingsData,
    hasInitialized,
    transferAnonymousConsentMutation,
  ]);

  // Check if banner was dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem("consent_banner_dismissed");
    if (dismissed) {
      setShowConsentBanner(false);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<ConsentContextType>(
    () => ({
      consent,
      privacySettings,
      isLoading,
      updateConsent,
      checkConsent,
      hasConsent,
      showConsentBanner,
      dismissBanner,
      refreshConsent,
      clearCache,
    }),
    [
      consent,
      privacySettings,
      isLoading,
      updateConsent,
      checkConsent,
      hasConsent,
      showConsentBanner,
      dismissBanner,
      refreshConsent,
      clearCache,
    ]
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};

// Hook to use consent context
// eslint-disable-next-line react-refresh/only-export-components
export function useConsent(): ConsentContextType {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
}
